import type { TaskCategoryCode } from '@liza/fhir-utils';
import { CarePlanMeasurementChart } from '@liza/ui/components/care-plan/CarePlanMeasurementChart';
import { cn } from '@liza/ui/utils';
import { formatDateOmitCurrentYear } from '@liza/ui/utils/date';
import { displayUnit, formatVitalValueParts, getComponentSeriesLabels } from '@liza/ui/utils/observation';
import { getTaskCategoryColor } from '@liza/ui/utils/ringCategories';
import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { MeasurementValue } from './MeasurementValue';

export type MeasurementDetailItemProps = {
  className?: string;
  /** Observation display name (e.g. "Blood pressure"). */
  name: string;
  /** All readings for this measurement, used to draw the trend chart. */
  results?: WithId<Observation>[];
  /** Latest reading, used for the headline value and timestamp. */
  latest?: WithId<Observation>;
  /** Human-readable schedule (e.g. "Daily (morning)"); omitted for as-needed / unscheduled. */
  scheduleLabel?: string;
  /** Optional task instructions shown beneath the name. */
  instructions?: string;
  /** Task category driving the icon fill; measurements fold to the clinical ring color. */
  category?: TaskCategoryCode | null;
  /** Localized unit that replaces the persisted (English) unit on the latest reading; used for
   * lifestyle measurements. Undefined leaves the observation's own unit unchanged. */
  unitOverride?: string;
  /** Shared epoch-ms time domain so every chart in the Results aligns on one axis. */
  domain?: [number, number];
  /** Ties this chart into the Results' synchronized hover group (shared crosshair/tooltip). */
  syncId?: string;
  /** Chart line color — pass the measurement's palette color so the stacked chart matches the combined view. */
  color?: string;
  /** When false, render only the header row (no chart) — used above the shared combined chart. */
  showChart?: boolean;
  /** When provided, the header (name/schedule/value) becomes a button that fires this to edit the
   * measurement Task. The chart is intentionally left outside the button so it stays interactive. */
  onEdit?: () => void;
  /** Test id applied to the edit button when `onEdit` is set. */
  editTestId?: string;
  /** The result code this measurement tracks, so the chart tooltip can name a sibling-coded reading. */
  trackedCode?: string;
  /** Set the headline value in the destructive color when the reading is flagged or out of its
   * reference range; see `MeasurementResultItem.flagAbnormal`. */
  flagAbnormal?: boolean;
};

/**
 * Detailed "Results" entry shown in the CarePlan topic detail overlay: the measurement header
 * (name, schedule, latest value) above a large labeled `CarePlanMeasurementChart`. The compact
 * row sparkline version is `MeasurementResultItem`. These are intentionally separate so the detail
 * view can grow richer behavior without affecting the column rows.
 */
export function MeasurementDetailItem({
  className,
  name,
  results,
  latest,
  scheduleLabel,
  instructions,
  category,
  unitOverride,
  domain,
  syncId,
  color,
  showChart = true,
  onEdit,
  editTestId,
  trackedCode,
  flagAbnormal,
}: MeasurementDetailItemProps): React.ReactElement {
  const { t } = useTranslation('records');
  const { value, unit: rawUnit } = latest
    ? formatVitalValueParts(latest, t('unknown'))
    : { value: '—', unit: undefined };
  const unit = displayUnit(!!latest, unitOverride, rawUnit);
  const categoryColor = getTaskCategoryColor(category);
  const hasChart = !!results && results.length >= 2;
  // Label the two lines (e.g. systolic / diastolic) so the chart tooltip isn't blank for composites.
  const seriesLabels = getComponentSeriesLabels(latest ?? results?.[0]);

  const header = (
    <>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="font-medium">{name}</span>
        {/* A step under the title, and the same size as the schedule beneath it — as the compact row
            sets its own instructions. */}
        {instructions && <span className="text-xs text-muted-foreground">{instructions}</span>}
        {scheduleLabel && <span className="text-xs text-muted-foreground">{scheduleLabel}</span>}
      </div>
      <div className="flex shrink-0 flex-col items-end text-right">
        <MeasurementValue
          value={value}
          latest={latest}
          flagAbnormal={flagAbnormal}
          className="text-base leading-none"
          testId="measurement-detail-value"
        />
        {(unit || latest?.effectiveDateTime) && (
          <span className="mt-0.5 text-xs whitespace-nowrap text-muted-foreground">
            {[unit, latest?.effectiveDateTime && formatDateOmitCurrentYear(latest.effectiveDateTime)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
      </div>
    </>
  );

  return (
    <div data-care-plan-item className={cn('flex flex-col gap-2 p-1 text-sm', className)}>
      {onEdit ? (
        <Button
          type="button"
          variant="ghost"
          onClick={onEdit}
          aria-label={name}
          data-testid={editTestId}
          className="flex h-auto w-full items-start justify-start gap-2.5 rounded-md p-0 text-left font-normal whitespace-normal hover:bg-accent/50"
        >
          {header}
        </Button>
      ) : (
        <div className="flex items-start gap-2.5">{header}</div>
      )}
      {showChart && hasChart && (
        <CarePlanMeasurementChart
          results={results}
          unit={unit}
          color={color ?? categoryColor ?? 'var(--color-muted-foreground)'}
          seriesLabels={seriesLabels}
          domain={domain}
          syncId={syncId}
          trackedCode={trackedCode}
        />
      )}
    </div>
  );
}
